import { useState } from "react";
import { Link, NavLink, Outlet, useLocation } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Phone, ChevronDown, Menu, X, CalendarCheck, Mail, MapPin, Star, ExternalLink } from "lucide-react";
import { BUSINESS, PAYMENT_METHODS, GOOGLE_REVIEW_WRITE_URL } from "@/data/site";
import { SERVICES } from "@/data/services";
import { COUNTIES, CITIES } from "@/data/locations";
import { LOCAL_BUSINESS_SCHEMA } from "@/components/Seo";
import { trackCall } from "@/lib/analytics";

const navLinkCls = ({ isActive }) =>
    `text-sm font-semibold transition-colors ${isActive ? "text-amber-400" : "text-slate-200 hover:text-white"}`;

function DropDown({ label, children, testid }) {
    return (
        <div className="relative group" data-testid={testid}>
            <button type="button" className="flex items-center gap-1 text-sm font-semibold text-slate-200 hover:text-white py-2" data-testid={`${testid}-toggle`}>
                {label} <ChevronDown className="h-3.5 w-3.5" />
            </button>
            <div className="absolute left-0 top-full hidden group-hover:block pt-1 z-40">
                <div className="w-64 rounded-xl border border-slate-700 bg-slate-900 shadow-2xl p-2">
                    {children}
                </div>
            </div>
        </div>
    );
}

const dropLink = "block rounded-lg px-3 py-2 text-sm text-slate-200 hover:bg-slate-800 hover:text-amber-400 transition-colors";

const FOOTER_GROUPS = [
    {
        label: "Services",
        links: SERVICES.map((s) => ({ label: s.name, to: `/${s.slug}` })),
    },
    {
        label: "Service Areas",
        links: [
            ...COUNTIES.map((c) => ({ label: `${c.name}, FL`, to: `/${c.slug}` })),
            ...CITIES.map((c) => ({ label: `${c.name}, FL`, to: `/${c.slug}` })),
        ],
    },
    {
        label: "Company",
        links: [
            { label: "Property Managers", to: "/property-managers" },
            { label: "Brands We Service", to: "/brands-we-service" },
            { label: "About", to: "/about" },
            { label: "Reviews", to: "/reviews" },
            { label: "FAQ", to: "/faq" },
            { label: "Request Service", to: "/request-service" },
            { label: "Leave Us a Google Review", to: GOOGLE_REVIEW_WRITE_URL, external: true },
        ],
    },
];

function FooterLink({ l, className }) {
    if (l.external) {
        return (
            <a href={l.to} target="_blank" rel="noopener noreferrer" className={`inline-flex items-center gap-1.5 ${className}`} data-testid="footer-review-link">
                {l.label} <ExternalLink className="h-3 w-3" />
            </a>
        );
    }
    return <Link to={l.to} className={className}>{l.label}</Link>;
}

export default function Layout() {
    const [open, setOpen] = useState(false);
    const location = useLocation();
    const close = () => setOpen(false);

    return (
        <div className="min-h-screen flex flex-col">
            <Helmet>
                <script type="application/ld+json">{JSON.stringify(LOCAL_BUSINESS_SCHEMA)}</script>
            </Helmet>

            <header className="bg-slate-950 text-white sticky top-0 z-50 shadow-lg">
                <div className="hidden sm:block border-b border-slate-800/80">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between py-1.5 text-xs">
                        <p className="text-slate-300">In-home appliance repair · Brevard &amp; Seminole Counties, FL</p>
                        <p className="text-slate-300">After-hours emergency service: 10 PM – 6 AM</p>
                    </div>
                </div>
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between gap-3 py-1.5 sm:py-2">
                    <Link to="/" className="flex items-center gap-3 shrink-0" data-testid="header-logo-link">
                        <img src="/logo.png" alt="Appliance Mania Service & Repair LLC logo" className="h-[52px] w-[52px] sm:h-16 sm:w-16 rounded-lg object-cover" width="52" height="52" />
                        <span className="hidden sm:block">
                            <span className="block font-heading font-extrabold tracking-tight text-amber-400 leading-none text-base sm:text-lg">APPLIANCE MANIA</span>
                            <span className="block font-heading font-bold text-red-500 text-[10px] sm:text-xs tracking-widest">SERVICE &amp; REPAIR LLC</span>
                        </span>
                    </Link>

                    <nav className="hidden lg:flex items-center gap-6" aria-label="Main navigation">
                        <NavLink to="/" className={navLinkCls} data-testid="nav-home">Home</NavLink>
                        <DropDown label="Services" testid="nav-services">
                            {SERVICES.map((s) => (
                                <Link key={s.slug} to={`/${s.slug}`} className={dropLink} data-testid={`nav-service-${s.slug}`}>{s.name}</Link>
                            ))}
                        </DropDown>
                        <DropDown label="Service Areas" testid="nav-areas">
                            {COUNTIES.map((c) => (
                                <Link key={c.slug} to={`/${c.slug}`} className={dropLink} data-testid={`nav-area-${c.slug}`}>{c.name}, FL</Link>
                            ))}
                            <div className="my-1 border-t border-slate-800" />
                            {CITIES.map((c) => (
                                <Link key={c.slug} to={`/${c.slug}`} className={dropLink} data-testid={`nav-city-${c.slug}`}>{c.name}, FL</Link>
                            ))}
                        </DropDown>
                        <NavLink to="/property-managers" className={navLinkCls} data-testid="nav-property-managers">Property Managers</NavLink>
                        <NavLink to="/brands-we-service" className={navLinkCls} data-testid="nav-brands">Brands</NavLink>
                        <NavLink to="/about" className={navLinkCls} data-testid="nav-about">About</NavLink>
                        <NavLink to="/faq" className={navLinkCls} data-testid="nav-faq">FAQ</NavLink>
                    </nav>

                    <div className="flex items-center gap-2">
                        <a href={BUSINESS.tel} onClick={() => trackCall("header")}
                            className="hidden md:inline-flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white font-bold px-5 py-2.5 rounded-xl shadow-lg shadow-red-600/25 transition-all duration-200 hover:-translate-y-0.5 text-sm"
                            data-testid="header-call-btn">
                            <Phone className="h-4 w-4" /> {BUSINESS.phone}
                        </a>
                        <a href={BUSINESS.tel} onClick={() => trackCall("header-mobile-icon")}
                            className="md:hidden inline-flex items-center justify-center bg-red-600 text-white rounded-xl p-2.5" aria-label="Call (321) 475-2230"
                            data-testid="header-call-icon-btn">
                            <Phone className="h-5 w-5" />
                        </a>
                        <button type="button" onClick={() => setOpen(!open)} className="lg:hidden p-2 text-slate-200" aria-label="Menu" aria-expanded={open} data-testid="mobile-menu-btn">
                            {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
                        </button>
                    </div>
                </div>

                {open && (
                    <nav className="lg:hidden border-t border-slate-800 max-h-[70vh] overflow-y-auto" aria-label="Mobile navigation" data-testid="mobile-menu">
                        <div className="px-4 py-4 space-y-1">
                            <MobileLink to="/" onClick={close} testid="mnav-home">Home</MobileLink>
                            <p className="pt-3 pb-1 text-[11px] font-bold uppercase tracking-widest text-amber-500/80">Services</p>
                            {SERVICES.map((s) => <MobileLink key={s.slug} to={`/${s.slug}`} onClick={close} testid={`mnav-service-${s.slug}`}>{s.name}</MobileLink>)}
                            <p className="pt-3 pb-1 text-[11px] font-bold uppercase tracking-widest text-amber-500/80">Service Areas</p>
                            {COUNTIES.map((c) => <MobileLink key={c.slug} to={`/${c.slug}`} onClick={close} testid={`mnav-area-${c.slug}`}>{c.name}, FL</MobileLink>)}
                            {CITIES.map((c) => <MobileLink key={c.slug} to={`/${c.slug}`} onClick={close} testid={`mnav-city-${c.slug}`}>{c.name}, FL</MobileLink>)}
                            <p className="pt-3 pb-1 text-[11px] font-bold uppercase tracking-widest text-amber-500/80">Company</p>
                            <MobileLink to="/property-managers" onClick={close} testid="mnav-property-managers">Property Managers</MobileLink>
                            <MobileLink to="/brands-we-service" onClick={close} testid="mnav-brands">Brands We Service</MobileLink>
                            <MobileLink to="/about" onClick={close} testid="mnav-about">About</MobileLink>
                            <MobileLink to="/reviews" onClick={close} testid="mnav-reviews">Reviews</MobileLink>
                            <MobileLink to="/faq" onClick={close} testid="mnav-faq">FAQ</MobileLink>
                            <MobileLink to="/request-service" onClick={close} testid="mnav-request-service" accent>Request Service</MobileLink>
                        </div>
                    </nav>
                )}
            </header>

            <main className="flex-1" key={location.pathname}>
                <Outlet />
            </main>

            <footer className="bg-slate-950 text-slate-300 dark-grain">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-9 pb-6">
                    <div className="flex flex-col sm:flex-row sm:items-center gap-5 sm:gap-10">
                        <div className="flex items-center gap-3 shrink-0">
                            <img src="/logo.png" alt="Appliance Mania Service & Repair LLC logo" className="h-14 w-14 rounded-lg object-cover" width="56" height="56" />
                            <span>
                                <span className="block font-heading font-extrabold text-amber-400 leading-none">APPLIANCE MANIA</span>
                                <span className="block font-heading font-bold text-red-500 text-[10px] tracking-widest">SERVICE &amp; REPAIR LLC</span>
                            </span>
                        </div>
                        <div className="text-sm">
                            <a href={BUSINESS.tel} onClick={() => trackCall("footer")} className="inline-flex items-center gap-2 text-lg font-heading font-extrabold text-white hover:text-amber-400 transition-colors" data-testid="footer-call-link">
                                <Phone className="h-5 w-5 text-red-500" /> {BUSINESS.phone}
                            </a>
                            <p className="mt-1.5 flex items-center gap-2 text-slate-300">
                                <Mail className="h-3.5 w-3.5 text-slate-400" />
                                <a href={`mailto:${BUSINESS.email}`} className="hover:text-white" data-testid="footer-email-link">{BUSINESS.email}</a>
                            </p>
                            <p className="mt-1.5 flex items-center gap-2 text-slate-300">
                                <MapPin className="h-3.5 w-3.5 text-slate-400" /> Serving Brevard County &amp; Seminole County, FL
                            </p>
                        </div>
                    </div>

                    <div className="lg:hidden mt-6 border-t border-slate-800 divide-y divide-slate-800" data-testid="footer-mobile-accordion">
                        {FOOTER_GROUPS.map((g) => (
                            <details key={g.label} className="group">
                                <summary className="flex items-center justify-between py-3.5 font-heading font-bold text-xs uppercase tracking-widest text-white cursor-pointer list-none select-none">
                                    {g.label}
                                    <ChevronDown className="h-4 w-4 text-slate-400 transition-transform group-open:rotate-180" />
                                </summary>
                                <div className="grid grid-cols-2 gap-x-4 pb-4">
                                    {g.links.map((l) => (
                                        <FooterLink key={l.to + l.label} l={l} className="py-1.5 text-sm text-slate-300 hover:text-amber-400 transition-colors" />
                                    ))}
                                </div>
                            </details>
                        ))}
                    </div>

                    <div className="hidden lg:grid lg:grid-cols-3 gap-10 mt-9">
                        {FOOTER_GROUPS.map((g) => (
                            <nav key={g.label} aria-label={`Footer ${g.label}`}>
                                <h2 className="font-heading font-bold text-white text-sm uppercase tracking-widest mb-4">{g.label}</h2>
                                <ul className="space-y-2 text-sm">
                                    {g.links.map((l) => (
                                        <li key={l.to + l.label}><FooterLink l={l} className="text-slate-300 hover:text-amber-400 transition-colors" /></li>
                                    ))}
                                </ul>
                            </nav>
                        ))}
                    </div>
                </div>
                <div className="border-t border-slate-800/80">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-5 pb-[calc(3.75rem+env(safe-area-inset-bottom))] sm:py-5 flex flex-col sm:flex-row items-center justify-between gap-2 text-xs text-slate-400">
                        <p>© {new Date().getFullYear()} {BUSINESS.name}. All rights reserved.</p>
                        <p data-testid="footer-payments">We accept: {PAYMENT_METHODS.join(" · ")}</p>
                    </div>
                </div>
            </footer>

            <div className="fixed bottom-0 left-0 right-0 z-50 bg-slate-900/95 backdrop-blur-md border-t border-slate-800 px-2.5 pt-2 pb-[max(0.5rem,env(safe-area-inset-bottom))] sm:hidden flex items-center gap-2 shadow-2xl" data-testid="mobile-sticky-bar">
                <a href={BUSINESS.tel} onClick={() => trackCall("sticky-bar")}
                    className="flex-1 inline-flex items-center justify-center gap-1.5 bg-red-600 active:bg-red-700 text-white font-bold py-2.5 px-2.5 rounded-lg text-[13px] whitespace-nowrap shadow-md"
                    data-testid="mobile-sticky-call-btn">
                    <Phone className="h-4 w-4 shrink-0" /> Call Now
                </a>
                <Link to="/request-service"
                    className="flex-1 inline-flex items-center justify-center gap-1.5 bg-amber-500 active:bg-amber-600 text-slate-950 font-bold py-2.5 px-2.5 rounded-lg text-[13px] whitespace-nowrap shadow-md"
                    data-testid="mobile-sticky-request-btn">
                    <CalendarCheck className="h-4 w-4 shrink-0" /> Request Service
                </Link>
            </div>
        </div>
    );
}

function MobileLink({ to, onClick, children, testid, accent = false }) {
    return (
        <Link to={to} onClick={onClick} className={`block rounded-lg px-3 py-2 text-sm hover:bg-slate-800 ${accent ? "font-bold text-amber-400 hover:text-amber-300" : "font-medium text-slate-200 hover:text-white"}`} data-testid={testid}>
            {children}
        </Link>
    );
}
